import {
  Bell,
  Bot,
  Check,
  LogOut,
  Monitor,
  Moon,
  PanelLeftOpen,
  Search,
  Sun,
  UserCircle2,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { StatusDot } from '@/components/common/StatusDot'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { cn } from '@/lib/cn'
import { useAuthStore } from '@/stores/auth'
import { useUIStore, type Theme } from '@/stores/ui'
import type { BotHealth } from '@/types/api'

import { flattenNavigation } from './navigation'

function Breadcrumb() {
  const location = useLocation()
  const items = flattenNavigation()

  // Longest matching prefix wins, so /tickets/42 resolves to Tickets rather
  // than to the first route that happens to start with a slash.
  const match = items
    .filter((item) => location.pathname === item.to || location.pathname.startsWith(`${item.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0]

  return (
    <div className="text-muted-foreground flex min-w-0 items-center gap-1.5 text-[13px]">
      <span className="hidden sm:inline">Unity RP</span>
      <span className="hidden sm:inline" aria-hidden>
        /
      </span>
      <span className="text-foreground truncate font-medium">{match?.label ?? 'Console'}</span>
    </div>
  )
}

function ThemeMenu() {
  const theme = useUIStore((state) => state.theme)
  const setTheme = useUIStore((state) => state.setTheme)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  const options: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'system', label: 'System', icon: Monitor },
  ]
  const Current = options.find((option) => option.value === theme)?.icon ?? Moon

  return (
    <div ref={ref} className="relative">
      <Button variant="ghost" size="icon-sm" onClick={() => setOpen(!open)} aria-label="Theme">
        <Current className="size-4" strokeWidth={1.75} />
      </Button>
      {open && (
        <div className="bg-overlay border-border elevated-lg animate-scale-in absolute right-0 z-50 mt-1 w-36 origin-top-right rounded-lg border p-1">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setTheme(option.value)
                setOpen(false)
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors',
                theme === option.value
                  ? 'text-primary-text bg-primary-soft'
                  : 'text-foreground/85 hover:bg-black/[0.04] dark:hover:bg-white/[0.06]',
              )}
            >
              <option.icon className="size-3.5" strokeWidth={1.75} aria-hidden />
              {option.label}
              {theme === option.value && <Check className="ml-auto size-3.5" strokeWidth={2.5} />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function AccountMenu() {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (event: MouseEvent) => {
      if (!ref.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  if (!user) return null

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="hover:ring-border-strong rounded-full ring-2 ring-transparent transition-all"
        aria-label="Account menu"
      >
        <Avatar src={user.avatar_url} name={user.display_name} size="md" />
      </button>

      {open && (
        <div className="bg-overlay border-border elevated-lg animate-scale-in absolute right-0 z-50 mt-1.5 w-60 origin-top-right overflow-hidden rounded-lg border">
          <div className="flex items-center gap-3 border-b border-border p-3">
            <Avatar src={user.avatar_url} name={user.display_name} size="lg" />
            <div className="min-w-0">
              <p className="truncate text-[13px] font-semibold">{user.display_name}</p>
              <p className="text-muted-foreground truncate text-[11.5px]">
                {user.is_superuser ? 'Superuser' : (user.primary_role_label ?? 'Staff')}
              </p>
            </div>
          </div>

          <div className="p-1">
            <Link
              to="/account"
              onClick={() => setOpen(false)}
              className="text-foreground/85 flex items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
            >
              <UserCircle2 className="size-3.5" strokeWidth={1.75} aria-hidden />
              Account & sessions
            </Link>
            <button
              onClick={() => void logout()}
              className="text-danger flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] transition-colors hover:bg-black/[0.04] dark:hover:bg-white/[0.06]"
            >
              <LogOut className="size-3.5" strokeWidth={1.75} aria-hidden />
              Sign out
            </button>
          </div>

          <div className="bg-surface-sunken/60 border-t border-border px-3 py-2">
            <p className="text-subtle-foreground text-[10.5px]">
              {user.permissions.length} permission{user.permissions.length === 1 ? '' : 's'} ·{' '}
              {user.roles.length} role{user.roles.length === 1 ? '' : 's'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}

export function Topbar({ bot }: { bot: BotHealth | null }) {
  const collapsed = useUIStore((state) => state.sidebarCollapsed)
  const toggleSidebar = useUIStore((state) => state.toggleSidebar)
  const openPalette = useUIStore((state) => state.setCommandPaletteOpen)

  // A handshake in progress reads as amber and pulsing: something is
  // happening and it is not a fault. Steady amber stays for a bot that is
  // connected but not ready, red for one that is not coming back on its own.
  const connecting = bot?.phase === 'connecting'

  const status = !bot?.enabled
    ? 'unknown'
    : bot.ready
      ? 'online'
      : connecting || bot.connected
        ? 'degraded'
        : 'offline'

  const statusLabel = !bot?.enabled
    ? 'Bot disabled'
    : bot.ready
      ? `Bot online${bot.latency_ms ? ` · ${Math.round(bot.latency_ms)}ms` : ''}`
      : (bot.status_reason ?? (connecting ? 'Bot connecting…' : 'Bot offline'))

  return (
    <header className="bg-surface/80 sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 backdrop-blur-md">
      {collapsed && (
        <Button variant="ghost" size="icon-sm" onClick={toggleSidebar} aria-label="Expand sidebar">
          <PanelLeftOpen className="size-4" strokeWidth={1.75} />
        </Button>
      )}

      <Breadcrumb />

      <div className="flex-1" />

      <button
        onClick={() => openPalette(true)}
        className={cn(
          'bg-surface-sunken border-border text-muted-foreground hidden items-center gap-2 rounded-md border',
          'hover:border-border-strong h-8 px-2.5 text-[12.5px] transition-colors md:flex',
        )}
      >
        <Search className="size-3.5" strokeWidth={1.75} aria-hidden />
        <span>Search</span>
        <kbd className="border-border ml-3 rounded border px-1 font-mono text-[10px]">⌘K</kbd>
      </button>

      <Tooltip content={statusLabel} side="bottom">
        <Link
          to="/bot"
          className="hover:border-border-strong border-border flex h-8 items-center gap-2 rounded-md border px-2.5 transition-colors"
        >
          <Bot className="text-muted-foreground size-3.5" strokeWidth={1.75} aria-hidden />
          <StatusDot status={status} pulse={status === 'online' || connecting} />
        </Link>
      </Tooltip>

      <Tooltip content="Activity" side="bottom">
        <Link to="/activity">
          <Button variant="ghost" size="icon-sm" aria-label="Activity">
            <Bell className="size-4" strokeWidth={1.75} />
          </Button>
        </Link>
      </Tooltip>

      <ThemeMenu />
      <AccountMenu />
    </header>
  )
}
